'use client';

import { cx } from 'class-variance-authority';
import { Icon, Modal } from '~/components/primitives';
import { I18N_CONF, ParsedLocale } from '@funkey/locales';
import { useI18n } from '~/features/i18n';
import { FLAG_ICONS, TRANSLATION_MAP } from './shared';
import { usePathname, useSearchParams } from 'next/navigation';
import { useMemo } from 'react';
import { UrlPath } from '~/utils/routing';

const LocaleSelector: React.FC<{
    className?: string;
}> = props => {
    const i18n = useI18n();
    const modal = Modal.useState();

    const CountryIcon = FLAG_ICONS[i18n.country];

    return (
        <>
            <button
                type="button"
                className={cx('flex flex-row items-center gap-3', props.className)}
                title={i18n._('Select language and country')}
                onClick={modal.open}
            >
                <div className="h-6 w-6 overflow-hidden rounded-full">
                    <CountryIcon />
                </div>
                <span className="font-bold text-white md:block">{i18n.language.toUpperCase()}</span>
            </button>

            {modal.isOpen && <LocaleSelectModal onClose={modal.close} />}
        </>
    );
};

export default LocaleSelector;

const LocaleSelectModal: React.FC<{
    onClose(): void;
}> = ({ onClose }) => {
    const i18n = useI18n();
    const pathname = usePathname();
    const searchParams = useSearchParams();

    // prettier-ignore
    const alternates = useMemo(
        () => [...document.querySelectorAll<HTMLLinkElement>('link[rel="alternate"]')]
            .reduce<Partial<Record<I18N_CONF.Locale, string>>>((acc, el) => {
                acc[el.hreflang as I18N_CONF.Locale] = el.href;
                return acc;
            }, {}),
        []
    );

    return (
        <Modal className="w-full max-w-sm font-medium" onClose={onClose}>
            <Modal.Header title={i18n._('Language')} className="pt-10" />
            <Modal.Content className="sm:pb-10">
                <menu className="flex flex-col gap-px overflow-hidden rounded-2xl">
                    {I18N_CONF.COUNTRY_WITH_LANGUAGES[i18n.country].map(language => {
                        const meta = TRANSLATION_MAP[language];
                        const locale = new ParsedLocale(language, i18n.country).toString();

                        const href = (() => {
                            const base = (() => {
                                if (Object.values(alternates).length !== 0) {
                                    return alternates[locale] || null;
                                }

                                const path = new UrlPath(pathname);
                                if (!I18N_CONF.isValidLocale(path.segments[0] ?? '')) {
                                    path.unshift(i18n.locale);
                                }

                                return path.setSegment(0, locale).toString();
                            })();

                            if (!base) return null;

                            const search = searchParams.toString();
                            return search
                                ? `${base}${base.includes('?') ? '&' : '?'}${search}`
                                : base;
                        })();

                        if (!href) return null;

                        return (
                            <a
                                key={locale}
                                className="group flex items-center justify-between bg-brown-50 p-4 transition hover:bg-brown-100"
                                href={href}
                            >
                                <span className="flex items-center">
                                    <meta.icon width={18} height={14} className="mr-3" />
                                    {meta.label}
                                </span>
                                <Icon
                                    name="arrow-right"
                                    className="text-secondary opacity-0 transition group-hover:opacity-100"
                                />
                            </a>
                        );
                    })}
                </menu>
            </Modal.Content>
        </Modal>
    );
};
